import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  listAll
} from 'firebase/storage';
import { getStorage } from 'firebase/storage';
import { db } from './firebase';
import { Inspection, InspectionFrame, FrameMarker } from '../types';
import { getFunctions, httpsCallable } from 'firebase/functions';

const storage = getStorage();

export class InspectionService {
  private static instance: InspectionService;
  private functions = getFunctions();

  static getInstance(): InspectionService {
    if (!InspectionService.instance) {
      InspectionService.instance = new InspectionService();
    }
    return InspectionService.instance;
  }

  // Inspection CRUD Operations
  async createInspection(inspectionData: Omit<Inspection, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'inspections'), {
        ...inspectionData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Inspection created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating inspection:', error);
      throw error;
    }
  }

  async updateInspection(inspectionId: string, updates: Partial<Inspection>): Promise<void> {
    try {
      const inspectionRef = doc(db, 'inspections', inspectionId);
      
      // Clean the drive folder data if present
      const cleanUpdates = { ...updates };
      if (cleanUpdates.driveFolder) {
        cleanUpdates.driveFolder = {
          id: cleanUpdates.driveFolder.id,
          name: cleanUpdates.driveFolder.name,
          url: cleanUpdates.driveFolder.url
        };
      }
      
      await updateDoc(inspectionRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Inspection updated:', inspectionId);
    } catch (error) {
      console.error('❌ Error updating inspection:', error);
      throw error;
    }
  }

  async deleteInspection(inspectionId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Delete the inspection document
      const inspectionRef = doc(db, 'inspections', inspectionId);
      batch.delete(inspectionRef);

      // Delete all frames in the inspection
      const framesQuery = query(collection(db, 'inspections', inspectionId, 'frames'));
      const framesSnapshot = await getDocs(framesQuery);
      framesSnapshot.forEach((frameDoc) => {
        batch.delete(frameDoc.ref);
      });

      await batch.commit();

      // Clean up storage files
      try {
        const videosRef = ref(storage, `inspections/${inspectionId}/videos`);
        const screenshotsRef = ref(storage, `inspections/${inspectionId}/screenshots`);
        const audioRef = ref(storage, `inspections/${inspectionId}/audio`);

        const deleteFolder = async (folderRef: any) => {
          try {
            const files = await listAll(folderRef);
            await Promise.all(files.items.map(item => deleteObject(item)));
          } catch (e) {
            // Folder might not exist, ignore
          }
        };

        await Promise.all([
          deleteFolder(videosRef),
          deleteFolder(screenshotsRef),
          deleteFolder(audioRef)
        ]);
      } catch (storageError) {
        console.warn('⚠️ Could not delete storage files:', storageError);
      }

      console.log('✅ Inspection and related data deleted:', inspectionId);
    } catch (error) {
      console.error('❌ Error deleting inspection:', error);
      throw error;
    }
  }

  async getInspection(inspectionId: string): Promise<Inspection | null> {
    try {
      const inspectionDoc = await getDoc(doc(db, 'inspections', inspectionId));
      
      if (!inspectionDoc.exists()) {
        return null;
      }

      const data = inspectionDoc.data();
      return {
        id: inspectionDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Inspection;
    } catch (error) {
      console.error('❌ Error getting inspection:', error);
      throw error;
    }
  }

  async getUserInspections(userId: string): Promise<Inspection[]> {
    try {
      const inspectionsQuery = query(
        collection(db, 'inspections'),
        where('createdBy', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      const snapshot = await getDocs(inspectionsQuery);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Inspection;
      });
    } catch (error) {
      console.error('❌ Error getting user inspections:', error);
      throw error;
    }
  }

  async getProjectInspections(projectId: string): Promise<Inspection[]> {
    try {
      const inspectionsQuery = query(
        collection(db, 'inspections'),
        where('projectId', '==', projectId),
        orderBy('updatedAt', 'desc')
      );
      const snapshot = await getDocs(inspectionsQuery);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Inspection;
      });
    } catch (error) {
      console.error('❌ Error getting project inspections:', error);
      throw error;
    }
  }

  // Frame Operations
  async addFrame(inspectionId: string, frameData: Omit<InspectionFrame, 'id' | 'createdAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'inspections', inspectionId, 'frames'), {
        ...frameData,
        createdAt: Timestamp.now()
      });
      
      console.log('✅ Frame added with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error adding frame:', error);
      throw error;
    }
  }

  async updateFrame(inspectionId: string, frameId: string, updates: Partial<InspectionFrame>): Promise<void> {
    try {
      const frameRef = doc(db, 'inspections', inspectionId, 'frames', frameId);
      await updateDoc(frameRef, updates);
      console.log('✅ Frame updated:', frameId);
    } catch (error) {
      console.error('❌ Error updating frame:', error);
      throw error;
    }
  }

  async deleteFrame(inspectionId: string, frameId: string): Promise<void> {
    try {
      // Get frame data first to clean up storage
      const frameDoc = await getDoc(doc(db, 'inspections', inspectionId, 'frames', frameId));
      if (frameDoc.exists()) {
        const frameData = frameDoc.data();
        
        // Delete associated storage files
        const deleteStorageFile = async (url: string) => {
          if (!url) return;
          try {
            // Extract path from URL and delete
            const urlObj = new URL(url);
            const path = decodeURIComponent(urlObj.pathname.split('/o/')[1]?.split('?')[0] || '');
            if (path) {
              await deleteObject(ref(storage, path));
            }
          } catch (e) {
            console.warn('⚠️ Could not delete storage file:', e);
          }
        };

        await Promise.all([
          deleteStorageFile(frameData.screenshotUrl),
          deleteStorageFile(frameData.markupUrl),
          deleteStorageFile(frameData.audioUrl)
        ]);
      }

      await deleteDoc(doc(db, 'inspections', inspectionId, 'frames', frameId));
      console.log('✅ Frame deleted:', frameId);
    } catch (error) {
      console.error('❌ Error deleting frame:', error);
      throw error;
    }
  }

  async getInspectionFrames(inspectionId: string): Promise<InspectionFrame[]> {
    try {
      const framesQuery = query(
        collection(db, 'inspections', inspectionId, 'frames'),
        orderBy('order', 'asc')
      );
      const snapshot = await getDocs(framesQuery);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date()
        } as InspectionFrame;
      });
    } catch (error) {
      console.error('❌ Error getting inspection frames:', error);
      throw error;
    }
  }

  async reorderFrames(inspectionId: string, frameIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      frameIds.forEach((frameId, index) => {
        const frameRef = doc(db, 'inspections', inspectionId, 'frames', frameId);
        batch.update(frameRef, { order: index });
      });

      await batch.commit();
      console.log('✅ Frames reordered');
    } catch (error) {
      console.error('❌ Error reordering frames:', error);
      throw error;
    }
  }

  // Storage Operations
  async uploadScreenshot(inspectionId: string, blob: Blob, filename: string): Promise<string> {
    try {
      const storageRef = ref(storage, `inspections/${inspectionId}/screenshots/${filename}`);
      await uploadBytes(storageRef, blob);
      const downloadUrl = await getDownloadURL(storageRef);
      console.log('✅ Screenshot uploaded:', filename);
      return downloadUrl;
    } catch (error) {
      console.error('❌ Error uploading screenshot:', error);
      throw error;
    }
  }

  async uploadMarkup(inspectionId: string, blob: Blob, filename: string): Promise<string> {
    try {
      const storageRef = ref(storage, `inspections/${inspectionId}/screenshots/${filename}`);
      await uploadBytes(storageRef, blob);
      const downloadUrl = await getDownloadURL(storageRef);
      console.log('✅ Markup uploaded:', filename);
      return downloadUrl;
    } catch (error) {
      console.error('❌ Error uploading markup:', error);
      throw error;
    }
  }

  async uploadAudio(inspectionId: string, blob: Blob, filename: string): Promise<string> {
    try {
      const storageRef = ref(storage, `inspections/${inspectionId}/audio/${filename}`);
      await uploadBytes(storageRef, blob);
      const downloadUrl = await getDownloadURL(storageRef);
      console.log('✅ Audio uploaded:', filename);
      return downloadUrl;
    } catch (error) {
      console.error('❌ Error uploading audio:', error);
      throw error;
    }
  }

  async uploadVideo(inspectionId: string, file: File): Promise<string> {
    try {
      const storageRef = ref(storage, `inspections/${inspectionId}/videos/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      console.log('✅ Video uploaded:', file.name);
      return downloadUrl;
    } catch (error) {
      console.error('❌ Error uploading video:', error);
      throw error;
    }
  }

  // Cloud Function Calls
  async transcribeAudio(inspectionId: string, frameId: string): Promise<string> {
    try {
      const transcribeFunc = httpsCallable(this.functions, 'transcribeAudio');
      const response = await transcribeFunc({ inspectionId, frameId });
      const result = response.data as { success: boolean; transcript?: string; message?: string };
      
      if (!result.success) {
        throw new Error(result.message || 'Transcription failed');
      }

      console.log('✅ Audio transcribed for frame:', frameId);
      return result.transcript || '';
    } catch (error) {
      console.error('❌ Error transcribing audio:', error);
      throw error;
    }
  }

  async generateReport(inspectionId: string): Promise<{ markdown: string; summary: string }> {
    try {
      const generateFunc = httpsCallable(this.functions, 'generateInspectionReport', { timeout: 300000 });
      const response = await generateFunc({ inspectionId });
      const result = response.data as { success: boolean; markdown?: string; summary?: string; message?: string };
      
      if (!result.success) {
        throw new Error(result.message || 'Report generation failed');
      }

      console.log('✅ Report generated for inspection:', inspectionId);
      return {
        markdown: result.markdown || '',
        summary: result.summary || ''
      };
    } catch (error) {
      console.error('❌ Error generating report:', error);
      throw error;
    }
  }

  async createGoogleDoc(inspectionId: string, markdown: string): Promise<string> {
    try {
      const createDocFunc = httpsCallable(this.functions, 'createInspectionDoc', { timeout: 120000 });
      const response = await createDocFunc({ inspectionId, markdown });
      const result = response.data as { success: boolean; docUrl?: string; message?: string };
      
      if (!result.success) {
        throw new Error(result.message || 'Google Doc creation failed');
      }

      console.log('✅ Google Doc created for inspection:', inspectionId);
      return result.docUrl || '';
    } catch (error) {
      console.error('❌ Error creating Google Doc:', error);
      throw error;
    }
  }

  // Marker Operations
  async updateFrameMarkers(inspectionId: string, frameId: string, markers: FrameMarker[]): Promise<void> {
    try {
      const frameRef = doc(db, 'inspections', inspectionId, 'frames', frameId);
      await updateDoc(frameRef, { markers });
      console.log('✅ Frame markers updated:', frameId);
    } catch (error) {
      console.error('❌ Error updating frame markers:', error);
      throw error;
    }
  }
}
